import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';

import LiveDotSvg from '../../../assets/desktop/home/live-dot.svg';
import OnlineBorderSvg from '../../../assets/desktop/home/online-border.svg';
import OnlineInnerGlowSvg from '../../../assets/desktop/home/online-inner-glow.svg';
import PlayNowImage from '../../../assets/homeRedesign/hero/play-now.png';
import UserIcon from '../../../assets/homeRedesign/user-icon.png';
import { selectTotalActiveUsersCount } from '../../../store/lobby/lobbySagas';
import { formatBigNumbers } from '../../../utils/helpers/numberFormat';
import { RootStackParamList } from '../../navigator';
import DailyMissionsCard from '../../shell/header/DailyMissionsCard';
import RewardsCard from '../../shell/header/RewardsCard';
import ScaledBox from '../common/ScaledBox';
import { Pressable } from '../common/Touchable';
import { getDesktopColumns } from '../Desktop/DesktopBottomBars';
import { DesktopJackpotBar } from '../Desktop/DesktopJackpotBar';
import { useDesktopLayout } from '../Desktop/desktopLayout';
import { DesktopRecentTablesBar } from '../Desktop/DesktopRecentTablesBar';
import CategoryCard from './CategoryCard';
import { HomeCard, useHomeCards } from './homeCards';

type Nav = NativeStackNavigationProp<RootStackParamList>;

// Figma "Home / Desktop" - "mackbook air" (1625:4506) on page "BOGI DRAFT", with the 1440
// (1648:252) and 1920 (1667:371) frames as the same layout scaled. Every number below is in the
// 1280 frame's space, measured from the "Content" frame's origin (32, 112).
//
// Two columns share one 592-high block: on the left the missions / free-chips pills, the Play
// Now hero and the recent-tables bar; on the right the 2x3 cards grid and the jackpot bar. The
// right column keeps its size at any width and the left column takes whatever is left - which
// is what the 1920 frame does. A taller viewport centres the block, as the 1440 frame does.
const BLOCK_HEIGHT = 592;
const COLUMN_GAP = 20;
const BARS_TOP = 510;
const PILLS = { top: 20, width: 216, height: 82.286 } as const;
/** The mobile pill the desktop one is drawn from: 126x48, scaled up by 216 / 126. */
const MOBILE_PILL = { width: 126, height: 48 } as const;
/** Figma "Play Now Button" - the mobile hero art, drawn 598x473 and 43.5 down the column. */
const HERO = { top: 43.5, width: 598, height: 473 } as const;
/** Figma "Players Online": a 112.8 disc starting 186 right of the hero's centre line. */
const ONLINE = { top: 122, leftFromHeroCenter: 186, size: 112.822 } as const;
/** Figma "Cards Grid": 576 wide, 22 down the right column. */
const GRID = { top: 22, width: 576, rowGap: 16 } as const;
const CARD_SMALL = { width: 166, height: 198, offsetTop: 23 } as const;
const CARD_LARGE = { width: 220, height: 221 } as const;
// The desktop cards are the mobile card stretched, so their plate values come from the Figma
// export they replaced: the small card blurs 20.77 with a 1.81 stroke and a darker fill, the
// large one blurs 21.33 with a 1.86 stroke. Glow units are blur / 10.
const PLATE_SMALL = {
	radius: 20.3,
	strokeWidth: 1.81,
	glowUnit: 2.077,
	fill: ['#7C0401', '#1C0702'],
} as const;
const PLATE_LARGE = { radius: 20.75, strokeWidth: 1.86, glowUnit: 2.133 } as const;
const CARD_GAP = 12;

const HomeDesktopContent = ({ onPlayNowPress }: { onPlayNowPress: () => void }) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation<Nav>();
	const onlineCount = useSelector(selectTotalActiveUsersCount);
	const { s, scale, side, contentWidth, contentHeight } = useDesktopLayout();
	// Two equal halves, as the 1280 and 1440 frames draw them; the cards grid keeps its size and
	// sits centred in the right half, the bars stretch to fill theirs.
	const { left: leftWidth, right: rightWidth } = getDesktopColumns(contentWidth, scale);
	const blockTop = Math.max(0, (contentHeight - s(BLOCK_HEIGHT)) / 2);
	const styles = createStyles(s, side, leftWidth, rightWidth, blockTop);
	const cards = useHomeCards();
	const pillScale = s(PILLS.width) / MOBILE_PILL.width;

	return (
		<View pointerEvents='box-none' style={styles.root}>
			<View pointerEvents='box-none' style={styles.left}>
				<Pressable onPress={onPlayNowPress} style={styles.hero}>
					<Image source={PlayNowImage} style={styles.heroImage} resizeMode='contain' />
				</Pressable>

				<View pointerEvents='none' style={styles.online}>
					<View style={styles.onlineGlow}>
						<OnlineInnerGlowSvg width='100%' height='100%' />
					</View>
					<View style={StyleSheet.absoluteFill}>
						<OnlineBorderSvg width='100%' height='100%' />
					</View>
					<Image source={UserIcon} style={styles.onlineIcon} resizeMode='contain' />
					<View style={styles.onlineStats}>
						<LiveDotSvg width={s(10.577)} height={s(10.566)} />
						<Text style={styles.onlineValue}>
							{formatBigNumbers(onlineCount ?? 0, { fractionDigits: 1 })}
						</Text>
					</View>
					<Text style={styles.onlineCaption}>
						{t('T_SITE_LOBBY_PLAYING_NOW', { defaultValue: 'playing now' })}
					</Text>
				</View>

				<View pointerEvents='box-none' style={styles.pills}>
					<ScaledBox width={MOBILE_PILL.width} height={MOBILE_PILL.height} scale={pillScale}>
						<DailyMissionsCard desktop />
					</ScaledBox>
					<ScaledBox width={MOBILE_PILL.width} height={MOBILE_PILL.height} scale={pillScale}>
						<RewardsCard desktop />
					</ScaledBox>
				</View>

				<View style={styles.bar}>
					<DesktopRecentTablesBar
						width={leftWidth}
						scale={scale}
						onPress={() => navigate('MyTables')}
					/>
				</View>
			</View>

			<View pointerEvents='box-none' style={styles.right}>
				<View pointerEvents='box-none' style={styles.grid}>
					<View style={styles.gridRow}>
						{cards.slice(0, 3).map(card => renderCard(card, styles, s))}
					</View>
					<View style={styles.gridRow}>
						{cards.slice(3).map(card => renderCard(card, styles, s))}
					</View>
				</View>

				<View style={styles.bar}>
					<DesktopJackpotBar width={rightWidth} scale={scale} />
				</View>
			</View>
		</View>
	);
};

const renderCard = (
	card: HomeCard,
	styles: ReturnType<typeof createStyles>,
	s: (v: number) => number,
) => {
	const large = !!card.featured;
	const plate = large ? PLATE_LARGE : PLATE_SMALL;

	return (
		<CategoryCard
			key={card.key}
			onPress={card.onPress}
			style={[large ? styles.cardLarge : styles.cardSmall, styles.cardPressable]}
			radius={s(plate.radius)}
			strokeWidth={s(plate.strokeWidth)}
			glowUnit={s(plate.glowUnit)}
			outerGlowOpacity={large ? 0.7 : 1}
			fill={'fill' in plate ? plate.fill : undefined}
		>
			<View style={styles.cardIcon}>
				<Image source={card.icon} style={StyleSheet.absoluteFill} resizeMode='contain' />
			</View>
			<View style={[styles.cardLabels, large && styles.cardLabelsLarge]}>
				<Text numberOfLines={1} style={styles.cardTitle}>
					{card.title}
				</Text>
				<Text numberOfLines={1} style={styles.cardMeta}>
					{card.meta}
				</Text>
			</View>
		</CategoryCard>
	);
};

const createStyles = (
	s: (v: number) => number,
	side: number,
	leftWidth: number,
	rightWidth: number,
	blockTop: number,
) =>
	StyleSheet.create({
		root: {
			...StyleSheet.absoluteFillObject,
		},
		left: {
			position: 'absolute',
			left: side,
			top: blockTop,
			width: leftWidth,
			height: s(BLOCK_HEIGHT),
		},
		right: {
			position: 'absolute',
			left: side + leftWidth + s(COLUMN_GAP),
			top: blockTop,
			width: rightWidth,
			height: s(BLOCK_HEIGHT),
		},
		pills: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: s(PILLS.top),
			flexDirection: 'row',
			justifyContent: 'space-between',
		},
		hero: {
			position: 'absolute',
			left: leftWidth / 2 - s(HERO.width / 2),
			top: s(HERO.top),
			width: s(HERO.width),
			height: s(HERO.height),
			cursor: 'pointer',
		} as any,
		heroImage: {
			width: '100%',
			height: '100%',
		},
		online: {
			position: 'absolute',
			left: leftWidth / 2 + s(ONLINE.leftFromHeroCenter),
			top: s(ONLINE.top),
			width: s(ONLINE.size),
			height: s(ONLINE.size),
			alignItems: 'center',
			paddingTop: s(12.96),
		},
		// The inner-glow art is exported with 8.1 of blur padding around the disc.
		onlineGlow: {
			position: 'absolute',
			left: s(-8.1),
			top: s(-8.1),
			width: s(129.022),
			height: s(128.908),
		},
		onlineIcon: {
			width: s(26.795),
			height: s(29.938),
			marginBottom: s(8.1),
		},
		onlineStats: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(3.24),
		},
		onlineValue: {
			fontFamily: 'Sofia Sans',
			fontWeight: '600',
			fontSize: s(20),
			lineHeight: s(22),
			color: '#FFFFFF',
		},
		onlineCaption: {
			marginTop: s(1.62),
			fontFamily: 'Sofia Sans',
			fontWeight: '500',
			fontSize: s(14),
			lineHeight: s(16),
			color: '#FFFF99',
		},
		bar: {
			position: 'absolute',
			left: 0,
			top: s(BARS_TOP),
		},
		grid: {
			position: 'absolute',
			left: (rightWidth - s(GRID.width)) / 2,
			width: s(GRID.width),
			top: s(GRID.top),
			rowGap: s(GRID.rowGap),
		},
		gridRow: {
			flexDirection: 'row',
			alignItems: 'flex-end',
			columnGap: s(CARD_GAP),
			height: s(CARD_LARGE.height),
		},
		cardPressable: {
			alignItems: 'center',
			cursor: 'pointer',
		} as any,
		cardSmall: {
			width: s(CARD_SMALL.width),
			height: s(CARD_SMALL.height),
			paddingVertical: s(16),
			rowGap: s(8),
		},
		cardLarge: {
			width: s(CARD_LARGE.width),
			height: s(CARD_LARGE.height),
			paddingTop: s(8),
			paddingBottom: s(18),
			rowGap: s(4),
		},
		cardIcon: {
			flex: 1,
			alignSelf: 'stretch',
			minHeight: 0,
		},
		cardLabels: {
			alignSelf: 'stretch',
			alignItems: 'center',
			paddingHorizontal: s(12),
			rowGap: s(10.385),
		},
		cardLabelsLarge: {
			paddingHorizontal: s(16),
			rowGap: s(10.667),
		},
		// Figma "Desktop/Card Title S": Sofia Sans Condensed SemiBold 24/1.1. At 24 the longest
		// titles, TOURNAMENTS and ACHIEVEMENTS, set 128 and 131 - inside the small card's 142.
		cardTitle: {
			fontFamily: 'Sofia Sans Condensed',
			fontWeight: '600',
			fontSize: s(24),
			lineHeight: s(26.4),
			color: '#FFFFFF',
			textAlign: 'center',
		},
		// Figma "Desktop/Card Meta S": Sofia Sans Medium 16/1.2 in text/accent at 75%.
		cardMeta: {
			fontFamily: 'Sofia Sans',
			fontWeight: '500',
			fontSize: s(16),
			lineHeight: s(19.2),
			color: '#FFFF99',
			opacity: 0.75,
			textAlign: 'center',
		},
	});

export default HomeDesktopContent;
